import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon issue with Vite
// Vite handles static assets differently than webpack - use direct URLs
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/node_modules/leaflet/dist/images/marker-icon-2x.png',
  iconUrl: '/node_modules/leaflet/dist/images/marker-icon.png',
  shadowUrl: '/node_modules/leaflet/dist/images/marker-shadow.png',
});

const AddressMap = ({ addresses, onEdit, onSave }) => {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(null);

  const getFullAddress = (address) => {
    return `${address.address || ''} ${address.address2 || ''}, ${address.city || ''}, ${address.state || ''} ${address.zip || ''}`;
  }

  useEffect(() => {
    if (addresses && addresses.length > 0) {
      const processAddresses = async () => {
        const initialMarkers = [];
        let initialMapCenter = null;
        const addressesToGeocode = [];

        // First pass: Add all addresses that already have coordinates
        for (const address of addresses) {
          const fullAddress = getFullAddress(address).trim();
          if (!fullAddress || fullAddress === ',') {
            continue;
          }

          if (address.lat && address.lon) {
            const position = [address.lat, address.lon];
            initialMarkers.push({
              ...address,
              position,
              popupContent: `${address.name}<br/>${fullAddress}`
            });
            if (!initialMapCenter) {
              initialMapCenter = position;
            }
          } else {
            addressesToGeocode.push({ address, fullAddress });
          }
        }

        // Show the map immediately with cached addresses
        setMarkers(initialMarkers);
        if (initialMapCenter) {
          setMapCenter(initialMapCenter);
        }
        setLoading(false);

        // Second pass: Geocode missing addresses in the background
        if (addressesToGeocode.length > 0) {
          const addressesToUpdate = [];

          for (const { address, fullAddress } of addressesToGeocode) {
            try {
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              let data = null;
              let position = null;
              
              // Try 1: Full address
              const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`;
              
              let response = await fetch(url);
              data = await response.json();
              
              
              // Try 2: If no results, try just city, state, zip (more general)
              if (!data || data.length === 0) {
                const fallbackAddress = `${address.city || ''}, ${address.state || ''} ${address.zip || ''}`.trim();
                if (fallbackAddress && fallbackAddress !== ',') {
                  await new Promise(resolve => setTimeout(resolve, 500));
                  
                  const fallbackUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fallbackAddress)}`;
                  response = await fetch(fallbackUrl);
                  data = await response.json();
                  
                }
              }
              
              if (data && data.length > 0) {
                const { lat, lon } = data[0];
                position = [parseFloat(lat), parseFloat(lon)];
                
                // Add to update list
                addressesToUpdate.push({ ...address, lat: position[0], lon: position[1] });
                
                // Add marker immediately
                setMarkers(prev => [...prev, {
                  ...address,
                  position,
                  popupContent: `${address.name}<br/>${fullAddress}`
                }]);
                
                // Set map center if this is the first marker
                if (!initialMapCenter) {
                  setMapCenter(position);
                  initialMapCenter = position;
                }
                
              } else {
              }
            } catch (error) {
            }
          }

          // Save all geocoded addresses to Firebase
          if (addressesToUpdate.length > 0 && onSave) {
            onSave(addressesToUpdate);
          }
        }
      };
      processAddresses();
    } else {
      setLoading(false);
    }
  }, [addresses]); // Removed onSave from dependencies

  if (loading) {
    return <div>Loading map and geocoding addresses...</div>;
  }

  if (!mapCenter) {
    return <div>No addresses to display on the map.</div>;
  }

  return (
    <MapContainer center={mapCenter} zoom={4} style={{ height: '600px', width: '100%' }}>
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      {markers.map((marker, idx) => (
        <Marker key={idx} position={marker.position}>
          <Popup>
            <div dangerouslySetInnerHTML={{ __html: marker.popupContent }} />
            <button onClick={() => onEdit(marker)}>Edit</button>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default AddressMap;
